import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { GridColumns, GridRowClassNameParams } from "@mui/x-data-grid-pro";
import { makeStyles } from "makeStyles";
import React, { useCallback, useMemo, useState } from "react";
import { RegistrationCommitteeItem } from "registration-committee-items";
import { completeableRowStyles, datagridStyles, expanderStyles } from "styles/common";
import { Link as RouterLink } from "react-router-dom";
import { MicrosoftWordLink } from "common/MicrosoftWordLink";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons/faArrowRight";
import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { useCollapsibleGrids } from "util/CollapsibleProvider";
import _ from "lodash";
import { DataGridWithHeader } from "common/DataGridWithHeader";
import { Box } from "@mui/system";
import { getRouteForRegistrationCommitteeItem, RegistrationCommitteeItemTabs } from "./RegistrationCommitteeItemScreen";
import { useMutation, useQuery } from "@apollo/client";
import { ApproveFinalReportMutation, FetchStaticFilesForRegComDecisionsQuery, ToggleFinalReportReviewedMutation } from "./queries";
import { Permissions, useCurrentUser } from "users";
import { ConfirmationDialog } from "common/ConfirmationDialog";
import { GetMeetingsForFinalDecisionQuery } from "committee-meetings";
import { useNotifications } from "notifications";
import { DocType } from "common/DocType";
import { useAppConfig } from "util/AppConfig";
import { useAxios } from "auth/SecureAxios";
import fileDownload from "js-file-download";
import { YesNoIcon } from "common/YesNoIcon";
import { isRegComItemDecisionIncomplete, isReviewDecisionIncomplete } from "decisions/decisionLogic";
import { formatDate } from "util/formats";
import { TooltipDataGridCell } from "common/TooltipDataGridCell";
import { LoadingButton } from "@mui/lab";
import { calculateItemCountsForCategoryInMode } from "./registrationCommitteeItemLogic";

const useStyles = makeStyles()((theme) => ({
  ...datagridStyles(theme),
  ...expanderStyles(theme),
  ...completeableRowStyles(theme),
  highDensityDataGridRoot: {
    "& .MuiDataGrid-columnHeader": {
      padding: "0 5px"
    },
    "& .MuiDataGrid-iconSeparator": {
      transform: "scale(0.5, 3)"
    }
  }
}));

interface Props {
  mode: "enter-committee-decisions" | "review-final-reports";
  selectedMeetingId: number;
  itemsForSelectedMeeting: RegistrationCommitteeItem[];
  decisionLettersGenerated: boolean;
  height?: number;
  renderPprpProgramReviews?: () => React.ReactNode;
}

const RegistrationCommitteeDecisionsTable: React.FunctionComponent<Props> = (props) => {
  const { selectedMeetingId, itemsForSelectedMeeting, decisionLettersGenerated } = props;
  const { classes, cx, theme } = useStyles();
  const { states, setExpanded } = useCollapsibleGrids();
  const user = useCurrentUser();
  const notifications = useNotifications();
  const appConfig = useAppConfig();
  const { fileDownloadAxios } = useAxios();

  const [finalReportsToApprove, setFinalReportsToApprove] = useState<RegistrationCommitteeItem[] | null>(null);
  const [finalReportPackageForItemIdDownloading, setFinalReportPackageForItemIdDownloading] = useState<number | null>(null);
  const [selectedStaticFiles, setSelectedStaticFiles] = useState<string[]>([]);

  const {
    data: staticFilesData,
    loading: staticFilesLoading,
    error: staticFilesError
  } = useQuery<{
    staticFilesForRegComDecisions: string[];
  }>(FetchStaticFilesForRegComDecisionsQuery);

  const [toggleFinalReportReviewedMutate, toggleFinalReportReviewedMutation] = useMutation<
    {
      registrationCommitteeItem: {
        toggleFinalReportReviewed: RegistrationCommitteeItem;
      };
    },
    {
      itemId: number;
      role: "ReadyForFinalReview" | "Registrar" | "Chair";
    }
  >(ToggleFinalReportReviewedMutation);

  const toggleReviewed = useCallback(
    async (item: RegistrationCommitteeItem, role: "ReadyForFinalReview" | "Registrar" | "Chair") => {
      await toggleFinalReportReviewedMutate({
        variables: {
          itemId: item.id,
          role
        },
        optimisticResponse: {
          registrationCommitteeItem: {
            toggleFinalReportReviewed: {
              ...item,
              isFinalReportReadyForReview:
                role === "ReadyForFinalReview" ? !item.isFinalReportReadyForReview : item.isFinalReportReadyForReview,
              isFinalReportReviewedByRegistrar:
                role === "Registrar" ? !item.isFinalReportReviewedByRegistrar : item.isFinalReportReviewedByRegistrar,
              isFinalReportReviewedByChair: role === "Chair" ? !item.isFinalReportReviewedByChair : item.isFinalReportReviewedByChair
            }
          }
        }
      });
    },
    [toggleFinalReportReviewedMutate]
  );

  const [approveFinalReportsMutate, approveFinalReportsMutation] = useMutation<
    {
      registrationCommitteeItem: {
        approveFinalReports: RegistrationCommitteeItem[];
      };
    },
    {
      itemIds: number[];
      staticFileNames?: string[];
    }
  >(ApproveFinalReportMutation);

  const approveFinalReports = useCallback(
    async (items: RegistrationCommitteeItem[], selectedStaticFiles: string[] = []) => {
      const result = await approveFinalReportsMutate({
        variables: {
          itemIds: items.map((item) => item.id),
          staticFileNames: selectedStaticFiles
        },
        refetchQueries: [
          {
            query: GetMeetingsForFinalDecisionQuery,
            variables: { forCoordinator: false, registrationCommittees: true }
          }
        ]
      });
      if ((result.data?.registrationCommitteeItem?.approveFinalReports?.length ?? 0) > 0) {
        notifications.success(`Item${items.length === 1 ? "" : "s"} ${items.map((item) => item.entityNumber).join(", ")} approved.`);
      }
    },
    [approveFinalReportsMutate, notifications]
  );

  async function downloadFinalReportPackage(item: RegistrationCommitteeItem) {
    setFinalReportPackageForItemIdDownloading(item.id);

    const getRequestEndpoint = `${appConfig.apiEndpoint}/api/registration-committee-item-document/final-report-package?registrationCommitteeItemId=${item.id}`;
    try {
      const getResult = await fileDownloadAxios.get(getRequestEndpoint);
      fileDownload(getResult.data, `Final Decision Package - ${item.registeredName}.pdf`);
    } catch (error: any) {
      let finalError = error;

      // If 401, try once more (token was refreshed by interceptor)
      if (error.response?.status === 401) {
        try {
          const retryResult = await fileDownloadAxios.get(getRequestEndpoint);
          fileDownload(retryResult.data, `Final Decision Package - ${item.registeredName}.pdf`);
          return;
        } catch (retryError: any) {
          finalError = retryError;
        }
      }

      // Handle the error
      if (typeof finalError.response?.data === "string") {
        notifications.error(new Error(finalError.response.data));
      } else {
        notifications.error(finalError);
      }
    } finally {
      setFinalReportPackageForItemIdDownloading(null);
    }
  }

  const approveFinalReportsConfirmationBody = (
    <Box>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Once you approve {finalReportsToApprove?.length === 1 ? "this item" : "these items"},{" "}
        {finalReportsToApprove?.length === 1 ? "it" : "they"} will be marked as completed and the decision cannot be changed.
      </Typography>

      {(staticFilesData?.staticFilesForRegComDecisions ?? []).length > 0 && (
        <Box>
          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" sx={{ mb: 1 }}>
            Additional Enclosures
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select preferred documents to attach to the final report email. These will be included alongside the decision letter.
          </Typography>

          {staticFilesLoading && (
            <Box display="flex" alignItems="center" justifyContent="center" py={2}>
              <CircularProgress size={24} sx={{ mr: 1 }} />
              <Typography>Loading available attachments...</Typography>
            </Box>
          )}

          {staticFilesError && (
            <Typography color="error" sx={{ mb: 2 }}>
              Error loading static files: {staticFilesError.message}
            </Typography>
          )}

          {(staticFilesData?.staticFilesForRegComDecisions || []).length > 0 && (
            <FormGroup>
              {(staticFilesData?.staticFilesForRegComDecisions || []).map((fileName) => (
                <FormControlLabel
                  key={fileName}
                  control={
                    <Checkbox
                      checked={selectedStaticFiles.includes(fileName)}
                      onChange={() => {
                        setSelectedStaticFiles((prev) =>
                          prev.includes(fileName) ? prev.filter((f) => f !== fileName) : [...prev, fileName]
                        );
                      }}
                      disabled={approveFinalReportsMutation.loading}
                    />
                  }
                  label={fileName}
                />
              ))}
            </FormGroup>
          )}
          <Box sx={{ minHeight: 30, pt: 1 }}>
            {selectedStaticFiles.length > 0 && (
              <Typography variant="body2" color="primary">
                {selectedStaticFiles.length} file{selectedStaticFiles.length !== 1 ? "s" : ""} selected for attachment
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );

  function groupAndSort<T>(items: T[], groupFn: (item: T) => number, sortFn: (group: T[]) => number): T[][] {
    const grouped = _.groupBy(items, groupFn);
    const groups = Object.values(grouped);
    return _.sortBy(groups, sortFn);
  }

  const groupedRegistrationCommitteeItems = useMemo(() => {
    // sort by registered name (should hold on the leaf level)
    const sortedItems = _.sortBy(itemsForSelectedMeeting, (item) => item.registeredName);
    // group by item type, sorted by type sortOrder
    const byType = groupAndSort(
      sortedItems,
      (item) => item.itemType.id,
      (group) => group[0].itemType.sortOrder
    );
    // group those groups by item category, sorted by category sortOrder
    const byCategory = groupAndSort(
      byType,
      (typeGroup) => typeGroup[0].itemType.category.id,
      (catGroup) => catGroup[0][0].itemType.category.sortOrder
    );
    return byCategory;
  }, [itemsForSelectedMeeting, props.mode]);

  const userCanClickApproveButton = user.userHasPermission(Permissions.RegComItemDecisionLetterFinalApproval);

  const getReasonItemCannotBeApproved = (item: RegistrationCommitteeItem) => {
    return item.isCompleted
      ? "This item has already been completed."
      : !item.isFinalReportReviewedByRegistrar
      ? "The item must be marked as reviewed by the Registrar."
      : !item.isFinalReportReviewedByChair &&
        (!item.pprpProgramReview || item.pprpProgramReview?.mostAuthoritativeDecision?.complianceZone?.requiresResponse === true)
      ? "The item must be marked as reviewed by the Chair."
      : !userCanClickApproveButton
      ? "You do not have permission to give final approval for these items."
      : isRegComItemDecisionIncomplete(item)
      ? "This item's decision is incomplete."
      : item.pprpProgramReview && isReviewDecisionIncomplete(item.pprpProgramReview)
      ? "The related PPRP Program Review's decision is incomplete."
      : "";
  };

  const itemsReadyForFinalApproval = itemsForSelectedMeeting.filter((rci) => !getReasonItemCannotBeApproved(rci));

  const getBlankCellWithTooltip = (tooltipText: string, placeHolderText: string = "--") => (
    <Tooltip title={tooltipText}>
      <Typography style={{ color: theme.palette.text.secondary, cursor: "help" }}>{placeHolderText}</Typography>
    </Tooltip>
  );

  const notYetReleasedMessage = "This item is not yet released for final review";

  const tableColumns: GridColumns<RegistrationCommitteeItem> = useMemo(
    () => [
      {
        field: "entityNumber",
        headerName: "Entity No.",
        headerClassName: classes.wrapHeader,
        valueGetter: ({ row }) => row.entityNumber
      },
      {
        field: "registeredName",
        headerName: "Registered Name",
        flex: 1,
        headerClassName: classes.wrapHeader,
        renderCell: ({ row }) => (
          <Tooltip title={row.registeredName}>
            <Link to={getRouteForRegistrationCommitteeItem(row, RegistrationCommitteeItemTabs.Overview)} component={RouterLink}>
              {row.registeredName}
            </Link>
          </Tooltip>
        )
      },
      {
        field: "contactName",
        headerName: "Contact Name",
        flex: 1,
        headerClassName: classes.wrapHeader
      },
      {
        field: "numAttachments",
        headerName: "Files Attached",
        headerClassName: classes.wrapHeader,
        valueGetter: ({ row }) => row.attachedDocuments.length
      },
      {
        field: "decision",
        headerName: "Decision",
        headerClassName: classes.wrapHeader,
        valueGetter: ({ row }) =>
          row.decision?.isApproved
            ? "Approved"
            : row.decision?.isDeferred
            ? "Deferred"
            : row.decision?.isRefused
            ? "Refused"
            : row.decision?.isTabled
            ? "Tabled"
            : "--"
      },
      {
        field: "generalNotes",
        headerName: "General Notes",
        headerClassName: classes.wrapHeader,
        width: 200,
        renderCell: ({ row }) => {
          return <TooltipDataGridCell notes={row.decision?.generalNotes} isRichText />;
        },
        align: "center"
      },
      {
        field: "writtenDecisionAttached",
        headerName: "Written Decision Attached",
        headerClassName: classes.wrapHeader,
        align: "center",
        renderCell: ({ row }) => {
          const isRequired = row.decision?.writtenDecisionRequired;
          const isAttached = row.attachedDocuments.some((ad) => ad.type === DocType.RegistrationCommitteeLegalCounselDecisionVerbiage);

          if (!isRequired && !isAttached) {
            return (
              <Tooltip title="Not required">
                <span>--</span>
              </Tooltip>
            );
          }

          const tooltip = isAttached ? "Attached" : "Required but not attached!";

          const icon = <YesNoIcon yes={isAttached} />;

          return (
            <Tooltip title={tooltip}>
              <Box component="span" sx={{ color: !isAttached ? "error.main" : undefined }}>
                {icon}
              </Box>
            </Tooltip>
          );
        }
      },
      {
        field: "monitoringRequired",
        headerName: "Monitoring Required",
        headerClassName: classes.wrapHeader,
        renderCell: ({ row }) => (row.decision ? <YesNoIcon yes={row.decision.monitoringRequired} /> : "--"),
        align: "center"
      },
      {
        field: "dueDate",
        headerName: "Due Date",
        headerClassName: classes.wrapHeader,
        valueGetter: ({ row }) => formatDate(row.dueDate)
      },
      {
        field: "decisionLetterLink",
        headerName: "Decision Letter",
        headerAlign: "center",
        headerClassName: classes.wrapHeader,
        width: 64,
        align: "center",
        sortable: false,
        hide: !decisionLettersGenerated,
        renderCell: ({ row }) => {
          const item = row;

          // if the item is not actionable on the screen, don't show the decision letter link
          if (props.mode === "review-final-reports" && !item.isFinalReportSentForReview) {
            return getBlankCellWithTooltip(notYetReleasedMessage);
          }

          const decisionLetter = item.attachedDocuments.filter((ad) => ad.type === DocType.RegistrationCommitteeItemDecisionLetter)[0];
          return decisionLetter ? (
            <MicrosoftWordLink href={decisionLetter.url}>{item.isCompleted ? "View" : "Edit"}</MicrosoftWordLink>
          ) : null;
        }
      },
      {
        field: "finalReportLink",
        headerName: "Final Decision Package",
        headerAlign: "center",
        headerClassName: classes.wrapHeader,
        align: "center",
        width: 86,
        sortable: false,
        hide: !decisionLettersGenerated,
        renderCell: ({ row }) => {
          const item = row;

          // if the item is not actionable on the screen, don't show the final package link
          if (props.mode === "review-final-reports" && !item.isFinalReportSentForReview) {
            return getBlankCellWithTooltip(notYetReleasedMessage);
          }

          const finalReportPackage = item.attachedDocuments.filter(
            (ad) => ad.type === DocType.RegistrationCommittteeItemFinalReportPackage
          )[0];
          return item.isCompleted && finalReportPackage?.url ? (
            <Link href={finalReportPackage.url} target="_blank">
              View
            </Link>
          ) : finalReportPackageForItemIdDownloading === item.id ? (
            <CircularProgress size="small" />
          ) : (
            <Link component="button" variant="body2" onClick={() => downloadFinalReportPackage(item)}>
              View
            </Link>
          );
        }
      },
      {
        field: "editDecisionLink",
        headerName: "Edit Decision",
        headerAlign: "center",
        headerClassName: classes.wrapHeader,
        align: "center",
        width: 62,
        sortable: false,
        renderCell: ({ row }) => {
          // if the item is not actionable on the screen, don't show the edit decision button
          if (props.mode === "review-final-reports" && !row.isFinalReportSentForReview) {
            return getBlankCellWithTooltip(notYetReleasedMessage);
          }

          return (
            <Tooltip
              title={
                isRegComItemDecisionIncomplete(row)
                  ? "This item's decision must be completed before a decision letter can be generated."
                  : "Edit Decision"
              }>
              <Link to={getRouteForRegistrationCommitteeItem(row, RegistrationCommitteeItemTabs.Decision)} component={RouterLink}>
                <IconButton color={isRegComItemDecisionIncomplete(row) ? "error" : "primary"} size="small">
                  <FontAwesomeIcon icon={faArrowRight} />
                </IconButton>
              </Link>
            </Tooltip>
          );
        }
      }
    ],
    [decisionLettersGenerated]
  );

  const reviewedColumns: GridColumns<RegistrationCommitteeItem> = useMemo(
    () =>
      props.mode === "review-final-reports"
        ? [
            {
              field: "isFinalReportReviewedByRegistrar",
              headerName: "Registrar Reviewed",
              headerAlign: "center",
              headerClassName: classes.wrapHeader,
              width: 72,
              align: "center",
              hide: !props.decisionLettersGenerated,
              renderCell: ({ row }) => {
                const item = row;

                // if the item is not actionable on the screen, don't show the Registrar review checkbox
                if (props.mode === "review-final-reports" && !item.isFinalReportSentForReview) {
                  return getBlankCellWithTooltip(notYetReleasedMessage);
                }

                const userCanToggle = user.userHasPermission(Permissions.RegComItemDecisionLetterRegistrarReview);
                const disabledReason = item.isCompleted
                  ? "This item has already been completed."
                  : !userCanToggle
                  ? "Only the Registrar can mark item as reviewed by Registrar."
                  : isRegComItemDecisionIncomplete(item)
                  ? "This item's decision is incomplete."
                  : "";

                return (
                  <Tooltip title={disabledReason}>
                    <span>
                      <Checkbox
                        checked={item.isFinalReportReviewedByRegistrar}
                        disabled={disabledReason !== ""}
                        onClick={() => toggleReviewed(item, "Registrar")}
                      />
                    </span>
                  </Tooltip>
                );
              }
            },
            {
              field: "isFinalReportReviewedByChair",
              headerName: "Chair Reviewed",
              headerAlign: "center",
              headerClassName: classes.wrapHeader,
              width: 72,
              align: "center",
              hide: !props.decisionLettersGenerated,
              renderCell: ({ row }) => {
                const item = row;

                // if the item is not actionable on the screen, don't show the Chair review checkbox
                if (props.mode === "review-final-reports" && !item.isFinalReportSentForReview) {
                  return getBlankCellWithTooltip(notYetReleasedMessage);
                }

                // for RCIs linked to PPRP that don't have serious deficiencies (complianceZone doesn't require response),
                // don't show the Chair review checkbox
                if (item.pprpProgramReview?.mostAuthoritativeDecision?.complianceZone?.requiresResponse === false) {
                  return getBlankCellWithTooltip("This PPRP item does not require a Chair review as there are no serious deficiencies.");
                }

                const userCanToggle = user.userHasPermission(Permissions.RegComItemDecisionLetterChairReview);
                const disabledReason = item.isCompleted
                  ? "This item has already been completed."
                  : !item.isFinalReportReviewedByRegistrar
                  ? "The Registrar has not reviewed the final report yet."
                  : !userCanToggle
                  ? "Only the Chair can mark item as reviewed by Chair."
                  : isRegComItemDecisionIncomplete(item)
                  ? "This item's decision is incomplete."
                  : "";

                return (
                  <Tooltip title={disabledReason}>
                    <span>
                      <Checkbox
                        checked={item.isFinalReportReviewedByChair}
                        disabled={disabledReason !== ""}
                        onClick={() => toggleReviewed(item, "Chair")}
                      />
                    </span>
                  </Tooltip>
                );
              }
            },
            {
              field: "Approve",
              headerName: "",
              align: "center",
              width: 120,
              sortable: false,
              hide: !props.decisionLettersGenerated,
              renderCell: ({ row }) => {
                const item = row;
                const disabledReason = getReasonItemCannotBeApproved(item);

                // replace the button with "Completed" if the item is already completed
                if (item.isCompleted) {
                  return <Typography className={classes.alreadySent}>Completed</Typography>;
                }

                // replace the button with "Pending" if the item is not yet released for final review
                if (props.mode === "review-final-reports" && !item.isFinalReportSentForReview) {
                  return getBlankCellWithTooltip(notYetReleasedMessage, "Pending");
                }

                return (
                  <Tooltip title={disabledReason}>
                    <span>
                      <Button variant="outlined" disabled={disabledReason !== ""} onClick={() => setFinalReportsToApprove([item])}>
                        Approve
                      </Button>
                    </span>
                  </Tooltip>
                );
              }
            }
          ]
        : [
            {
              field: "isFinalReportReadyForReview",
              headerName: "Ready to Release?",
              headerClassName: classes.wrapHeader,
              headerAlign: "center",
              width: 72,
              align: "center",
              hide: !props.decisionLettersGenerated,
              renderCell: ({ row }) => {
                const item = row;

                // this can't be changed anymore once you've sent it off, which can happen one at a time for reg com,
                // so we swap it out with a permanent mark
                if (row.isFinalReportSentForReview) {
                  return (
                    <Tooltip title="Already sent for final review">
                      <span>
                        <FontAwesomeIcon icon={faCheck} className={classes.alreadySent} />
                      </span>
                    </Tooltip>
                  );
                }
                return (
                  <Tooltip title={isRegComItemDecisionIncomplete(item) ? "This item's decision is incomplete." : ""}>
                    <span>
                      <Checkbox
                        checked={item.isFinalReportReadyForReview}
                        disabled={isRegComItemDecisionIncomplete(item)}
                        onClick={() => toggleReviewed(item, "ReadyForFinalReview")}
                      />
                    </span>
                  </Tooltip>
                );
              }
            }
          ],
    [props.decisionLettersGenerated, toggleReviewed, user]
  );

  const storageKey = `CommitteeDecisions:${selectedMeetingId} RegistrationCommitteeItems`;
  const expanded = states[storageKey] ?? true;

  return (
    <>
      <Stack direction="row" spacing={1}>
        <IconButton size="small" onClick={() => setExpanded(storageKey, !expanded)}>
          <FontAwesomeIcon icon={faChevronDown} className={cx(classes.expandedIcon, { rotated: expanded })} />
        </IconButton>
        <Typography variant="h2">Committee Items</Typography>
      </Stack>
      <Collapse in={expanded}>
        <Paper sx={(theme) => ({ p: 2, [theme.breakpoints.up("lg")]: { height: props.height, overflow: "auto" } })}>
          <Stack spacing={5}>
            {groupedRegistrationCommitteeItems.map((group) => {
              const counts = calculateItemCountsForCategoryInMode(group, props.mode);

              const isPprpProgramReviewType = group[0][0].itemType.isPprpProgramReviewType;
              const category = group[0][0].itemType.category.name;
              const categoryStorageKey = `${storageKey} $category`;
              const categoryExpanded = states[categoryStorageKey] ?? true;
              return (
                <div>
                  <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                    <IconButton size="small" onClick={() => setExpanded(categoryStorageKey, !categoryExpanded)}>
                      <FontAwesomeIcon icon={faChevronDown} className={cx(classes.expandedIcon, { rotated: categoryExpanded })} />
                    </IconButton>
                    <Typography variant="h3" color="primary">
                      {category}
                    </Typography>
                    <Typography color="primary">
                      {counts.descriptions.actionable} {counts.descriptions.formattedNonActionable}
                    </Typography>
                  </Stack>
                  <Collapse in={categoryExpanded}>
                    <Stack spacing={2}>
                      {group.map((items) => {
                        return (
                          <Box sx={{ pl: 3, pr: 3 }}>
                            <DataGridWithHeader
                              title={<Typography variant="h4">{items[0].itemType.name}</Typography>}
                              headerAlign="center"
                              allowHidingColumns
                              displayWithoutContainer
                              deemphasizeHeader
                              collapsible
                              storageKey={`${categoryStorageKey} - ${items[0].itemType.name}`}
                              className={cx([classes.datagridRoot, classes.highDensityDataGridRoot])}
                              disableSelectionOnClick
                              getRowClassName={(rowParams: GridRowClassNameParams<RegistrationCommitteeItem>) => {
                                if (props.mode === "enter-committee-decisions") {
                                  return rowParams.row.isFinalReportSentForReview ? classes.completedRow : "";
                                }
                                if (props.mode === "review-final-reports") {
                                  return rowParams.row.isCompleted
                                    ? classes.completedRow
                                    : !rowParams.row.isFinalReportSentForReview
                                    ? classes.unactionableRow
                                    : "";
                                }
                                return "";
                              }}
                              rows={items}
                              columns={[
                                ...(isPprpProgramReviewType
                                  ? [
                                      {
                                        field: "prNumber",
                                        headerName: "PR#",
                                        width: 70,
                                        headerClassName: classes.wrapHeader,
                                        valueGetter: ({ row }: { row: RegistrationCommitteeItem }) => row.pprpProgramReview?.prNumber
                                      }
                                    ]
                                  : []),
                                ...tableColumns,
                                ...reviewedColumns
                              ]}
                              pinnedColumns={{
                                left: ["entityNumber", ...(isPprpProgramReviewType ? ["prNumber"] : []), "registeredName"]
                              }}
                            />
                            {isPprpProgramReviewType && props.renderPprpProgramReviews && (
                              <Box sx={{ pl: 6, pt: 3 }}>{props.renderPprpProgramReviews()}</Box>
                            )}
                          </Box>
                        );
                      })}
                    </Stack>
                  </Collapse>
                </div>
              );
            })}
          </Stack>
        </Paper>
      </Collapse>

      {props.decisionLettersGenerated && props.mode === "review-final-reports" && itemsReadyForFinalApproval.length > 0 && (
        <Stack direction="row" spacing={2} sx={{ alignSelf: "flex-end" }}>
          <LoadingButton
            color="primary"
            variant="outlined"
            loading={approveFinalReportsMutation.loading || toggleFinalReportReviewedMutation.loading}
            disabled={approveFinalReportsMutation.loading || toggleFinalReportReviewedMutation.loading || !userCanClickApproveButton}
            onClick={() => {
              setFinalReportsToApprove(itemsReadyForFinalApproval);
            }}>
            Approve All Reviewed Items
          </LoadingButton>
        </Stack>
      )}

      <ConfirmationDialog
        open={!!finalReportsToApprove}
        title="Approve final decision and/or decision letters?"
        body={approveFinalReportsConfirmationBody}
        confirmButtonText="Continue"
        noDanger
        loading={approveFinalReportsMutation.loading}
        confirm={async () => {
          await approveFinalReports(finalReportsToApprove!, selectedStaticFiles);
          setFinalReportsToApprove(null);
          setSelectedStaticFiles([]);
        }}
        cancel={() => {
          setFinalReportsToApprove(null);
          setSelectedStaticFiles([]);
        }}
      />
    </>
  );
};

export default RegistrationCommitteeDecisionsTable;
